import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin } from "lucide-react";
import Logo from "@/components/Logo";
import { useSettings } from "@/hooks/useSettings";

export default function Footer() {
  const { settings } = useSettings();
  return (
    <footer className="bg-ink text-cream/85" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16">
        <div className="grid gap-12 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2.5">
              <Logo size={36} showText={false} />
              <span className="font-display text-2xl font-semibold text-cream">The Dyslexia Tutor</span>
            </div>
            <p className="mt-4 max-w-md text-cream/70 text-sm leading-relaxed">
              Specialized 1:1 reading tutoring for kids with dyslexia, ADHD, and learning differences.
              Evidence-based, Orton-Gillingham, parent-trusted — by Jonna Chrisman, M.Ed.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {settings?.linkedin_url && (
                <a
                  href={settings.linkedin_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 hover:bg-cream/10 transition"
                  data-testid="footer-linkedin"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={16} />
                </a>
              )}
            </div>
          </div>
          <div>
            <h4 className="font-display text-base font-semibold text-cream">Explore</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/about" className="hover:text-cream link-underline" data-testid="footer-link-about">About Jonna</Link></li>
              <li><Link to="/services" className="hover:text-cream link-underline" data-testid="footer-link-services">Services & Pricing</Link></li>
              <li><Link to="/approach" className="hover:text-cream link-underline" data-testid="footer-link-approach">Our Approach</Link></li>
              <li><Link to="/success-stories" className="hover:text-cream link-underline" data-testid="footer-link-stories">Success Stories</Link></li>
              <li><Link to="/faq" className="hover:text-cream link-underline" data-testid="footer-link-faq">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-cream link-underline" data-testid="footer-link-contact">Join Waitlist</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-base font-semibold text-cream">Get in touch</h4>
            <ul className="mt-4 space-y-3 text-sm">
              {settings?.business_email ? (
                <li className="flex items-start gap-2">
                  <Mail size={16} className="mt-0.5 flex-none" />
                  <a href={`mailto:${settings.business_email}`} className="hover:text-cream link-underline" data-testid="footer-email">
                    {settings.business_email}
                  </a>
                </li>
              ) : (
                <li className="flex items-start gap-2 text-cream/50 italic" data-testid="footer-email-pending">
                  <Mail size={16} className="mt-0.5 flex-none" />
                  Email — coming soon
                </li>
              )}
              {settings?.business_phone ? (
                <li className="flex items-start gap-2">
                  <Phone size={16} className="mt-0.5 flex-none" />
                  <a href={`tel:${settings.business_phone}`} className="hover:text-cream link-underline" data-testid="footer-phone">
                    {settings.business_phone}
                  </a>
                </li>
              ) : (
                <li className="flex items-start gap-2 text-cream/50 italic" data-testid="footer-phone-pending">
                  <Phone size={16} className="mt-0.5 flex-none" />
                  Phone — coming soon
                </li>
              )}
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 flex-none" />
                <span>{settings?.service_area || "Springboro, OH"}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-14 pt-8 border-t border-cream/10 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-cream/55">
          <p>© {new Date().getFullYear()} The Dyslexia Tutor · Jonna Chrisman, M.Ed.</p>
          <Link to="/admin/login" className="hover:text-cream/80" data-testid="footer-admin-link">Admin</Link>
        </div>
      </div>
    </footer>
  );
}
